import React from "react";
import useVisitorCounter from "../../../../hooks/UseVisitorCounter";
import {
  Eye,
  TrendingUp,
  RefreshCw,
  Monitor,
  Calendar,
  BarChart3,
  Users,
} from "lucide-react";

const pageNames = {
  "/": "Home",
  "/about": "About",
  "/research": "Research",
  "/service": "Service",
  "/product": "Product",
  "/product/all-product": "All Products",
  "/blog": "Blog",
  "/contact": "Contact",
};

const VisitorDashboard = () => {
  const { stats, loading, error, fetchVisitorStats } = useVisitorCounter();

  const StatCard = ({
    title,
    value,
    icon: Icon,
    subtitle,
    color = "primary",
  }) => {
    const colorClasses = {
      primary: "bg-[#4B80B6]/10 border-[#4B80B6]/20 text-[#4B80B6]",
      secondary: "bg-[#3A658F]/10 border-[#3A658F]/20 text-[#3A658F]",
      accent: "bg-[#4B80B6]/5 border-[#4B80B6]/15 text-[#3A658F]",
      light: "bg-gray-50 border-gray-200 text-gray-700",
    };

    return (
      <div
        className={`rounded-lg border p-4 ${colorClasses[color]} transition-all hover:shadow-sm`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className="p-2 rounded-full bg-white/50">
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </div>
    );
  };

  const WeeklyVisitorChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map((item) => item.pageViews)) || 100;

    return (
      <div className="mt-6">
        <div className="flex items-end justify-between h-48 px-4 py-2 bg-gradient-to-t from-gray-50 to-white rounded-lg border">
          {data.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center space-y-2 flex-1"
            >
              <div className="relative w-full flex flex-col items-center justify-end h-36">
                <div
                  className="w-8 bg-gradient-to-t from-[#4B80B6] to-[#4B80B6]/80 rounded-t-sm shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  style={{
                    height: `${Math.max(
                      (item.pageViews / maxValue) * 100,
                      5
                    )}%`,
                    minHeight: "8px",
                  }}
                  title={`${item.pageViews} views on ${item.day}`}
                >
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {item.pageViews} views
                  </div>
                </div>
              </div>
              <span className="text-xs font-medium text-gray-600">
                {item.day}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-4">
          <div className="flex items-center bg-[#4B80B6]/10 px-3 py-1 rounded-full">
            <div className="w-3 h-3 bg-gradient-to-r from-[#4B80B6] to-[#4B80B6]/80 rounded-full mr-2"></div>
            <span className="text-xs font-medium text-[#4B80B6]">
              Daily Page Views
            </span>
          </div>
        </div>
      </div>
    );
  };

  const TopPagesList = ({ pages }) => {
    if (!pages || pages.length === 0) return null;

    return (
      <div className="space-y-3">
        {pages
          .sort((a, b) => b.pageViews - a.pageViews)
          .slice(0, 3)
          .map((page, index) => {
            const percentage = (page.pageViews / pages[0].pageViews) * 100;
            return (
              <div key={index} className="relative">
                <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        index === 0
                          ? "bg-[#4B80B6]"
                          : index === 1
                          ? "bg-[#3A658F]"
                          : index === 2
                          ? "bg-[#4B80B6]/70"
                          : "bg-gray-400"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {pageNames[page.page] || page.page}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-[#4B80B6]">
                      {page.pageViews.toLocaleString()}
                    </span>
                    <div className="text-xs text-gray-500">views</div>
                  </div>
                </div>
                <div
                  className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#4B80B6] to-[#4B80B6]/60 rounded-b-lg transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            );
          })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-5 h-5 animate-spin text-[#4B80B6]" />
          <span className="text-gray-600">Loading visitor statistics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Monitor className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-red-600 font-medium">Error loading visitor data</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <button
            onClick={fetchVisitorStats}
            className="mt-3 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="visitor-dashboard space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Visitor Analytics
          </h2>
          <p className="text-sm text-gray-500">
            Real-time website traffic overview
          </p>
        </div>
        <button
          onClick={fetchVisitorStats}
          className="flex items-center space-x-2 px-4 py-2 bg-[#4B80B6] hover:bg-[#3A658F] text-white rounded-lg transition-colors text-sm font-medium shadow-sm hover:shadow-md"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Page Views"
          value={stats.totalPageViews.toLocaleString()}
          icon={Eye}
          subtitle="All time page visits"
          color="primary"
        />
        <StatCard
          title="Unique Pages"
          value={stats.pageStats.length}
          icon={Monitor}
          subtitle="Different pages tracked"
          color="secondary"
        />
        <StatCard
          title="Weekly Average"
          value={Math.round(stats.totalPageViews / 7).toLocaleString()}
          icon={Calendar}
          subtitle="Views per day"
          color="accent"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-gray-50 rounded-lg p-6 border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-gray-600" />
              <h3 className="text-base font-semibold text-gray-800">
                Most Popular Pages
              </h3>
            </div>
            <div className="bg-[#4B80B6]/10 text-[#4B80B6] text-xs px-2 py-1 rounded-full font-medium">
              Top 3
            </div>
          </div>
          <TopPagesList pages={stats.pageStats} />
        </div>

        {/* Weekly Chart */}
        <div className="bg-gray-50 rounded-lg p-6 border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-[#3A658F]" />
              <h3 className="text-base font-semibold text-gray-800">
                Weekly Trends
              </h3>
            </div>
            <div className="bg-[#3A658F]/10 text-[#3A658F] text-xs px-2 py-1 rounded-full font-medium">
              Last 7 Days
            </div>
          </div>
          <WeeklyVisitorChart data={stats.weeklyData} />
        </div>
      </div>

      {/* Summary Footer */}
      <div className="bg-gradient-to-r from-[#4B80B6]/10 to-[#3A658F]/10 rounded-lg p-4 border border-[#4B80B6]/20">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-[#4B80B6] rounded-full flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">
              Your website has received{" "}
              <span className="font-bold text-[#4B80B6]">
                {stats.totalPageViews.toLocaleString()}
              </span>{" "}
              page views
            </p>
            <p className="text-xs text-gray-600">
              Across {stats.pageStats.length} different pages with an average of{" "}
              {Math.round(stats.totalPageViews / stats.pageStats.length)} views
              per page
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorDashboard;
